/**
 * Reading untyped JSON off the wire.
 *
 * Every `*Source.ts` adapter has the same job at its boundary: take whatever the
 * API actually returned — which TypeScript cannot know — and hand the app a
 * value of the type it declared. The adapters used to spell that boundary
 * `Record<string, any>`, which is the one annotation that makes the compiler
 * stop asking questions: `raw.dispaly_name` type-checks, `raw.quantity` flows
 * into a `string` field, and a wire shape that changed under us produces a
 * `undefined` on screen rather than an error anywhere.
 *
 * `Record<string, unknown>` is the same claim ("an object whose keys I do not
 * know") minus the lie ("...and whose values are whatever you need them to
 * be"). It costs one coercion per field read, which is what these helpers are.
 *
 * The five adapters that each carried a private copy of {@link rowsFromWire}
 * are why this is a module rather than a convention: the copies had drifted —
 * one of them returned `data.items` without checking it was an array, so a
 * non-array `items` reached `.map()` and threw. One implementation cannot drift
 * from itself.
 */

/** Narrow to an object with unknown values. Arrays are objects — see {@link asRecord}. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** An object, or `{}` — for reading a nested bag whose absence means "empty". */
export function asRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

/**
 * A success payload as a plain array of rows.
 *
 * Accepts both shapes our APIs return: a bare array, and the paged
 * `{items, next_cursor}` body. Anything else — including a truthy non-array
 * `items` — yields `[]` rather than a value that throws at the first `.map()`.
 */
export function rowsFromWire(data: unknown): Record<string, unknown>[] {
  const rows = Array.isArray(data) ? data : isRecord(data) ? data.items : undefined;
  return Array.isArray(rows) ? rows.filter(isRecord) : [];
}

/** An array of rows under `key`, or `[]`. For row fields that nest a list. */
export function rowsAt(raw: Record<string, unknown>, key: string): Record<string, unknown>[] {
  const value = raw[key];
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

/** A string, always. `null`/`undefined` become {@link fallback}. */
export function asString(value: unknown, fallback = ""): string {
  return value == null ? fallback : String(value);
}

/** A string, or `null` when the field was absent. Mirrors the old `raw.x ?? null`. */
export function asNullableString(value: unknown): string | null {
  return value == null ? null : String(value);
}

/** A string, or `undefined` when the field was absent. Mirrors the old `raw.x ?? undefined`. */
export function asOptString(value: unknown): string | undefined {
  return value == null ? undefined : String(value);
}

/** A finite number, or `null` — a non-numeric string is absence, not `NaN`. */
export function asNumber(value: unknown): number | null {
  if (value == null) return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** A string array — non-string members are dropped, not stringified. */
export function asStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}
